export type PageArchetype =
  | 'HOME' | 'REPAIR_ROOT' | 'REPAIR_BRAND' | 'REPAIR_MODEL'
  | 'REPAIR_MODEL_SERVICE' | 'PHONE_CATALOG_ROOT' | 'ACCESSORY_CATALOG_ROOT' | 'PRODUCT_PHONE'
  | 'PRODUCT_ACCESSORY' | 'PRODUCT_OTHER' | 'BLOG_ROOT' | 'BLOG_ARTICLE'
  | 'DEVICE_ROOT' | 'DEVICE_CATEGORY' | 'DEVICE_BRAND' | 'DEVICE_MODEL'
  | 'RACHAT' | 'CONTACT' | 'APPOINTMENT_STATE' | 'STATIC' | 'LEGACY_STATE';

export interface ResolvedPageContext {
  readonly version: 1;
  readonly siteId: number;
  readonly tenantId: number;
  readonly marketId: number;
  readonly locale: string;
  readonly pageArchetype: PageArchetype;
  readonly intentType: string;
  readonly brandId: string | null;
  readonly modelId: string | null;
  readonly masterServiceId: string | null;
  readonly masterServiceReferenceId: number | null;
  readonly modelServiceRelationshipId: number | null;
  readonly productId: string | null;
  readonly blogPostId: string | null;
  readonly deviceCategoryId: string | null;
  readonly slugs: Readonly<{
    brand?: string;
    model?: string;
    masterService?: string;
    product?: string;
    blogPost?: string;
    category?: string;
    buybackModel?: string;
    static?: string;
  }>;
  readonly canonicalPath: string;
  readonly canonicalUrl: string;
  readonly requestedPath: string;
  readonly redirectRequired: boolean;
  readonly legacyResolution: Readonly<{ kind: string; requestedPath: string }> | null;
  readonly queryParameters: ReadonlyArray<Readonly<{
    name: string;
    value: string;
    classification: 'IDENTITY_LEGACY' | 'TRANSACTION_STATE' | 'FILTER' | 'TRACKING';
  }>>;
}

export const isResolvedPageContext = (value: unknown): value is ResolvedPageContext => {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<ResolvedPageContext>;
  return candidate.version === 1
    && typeof candidate.siteId === 'number'
    && typeof candidate.pageArchetype === 'string'
    && typeof candidate.canonicalPath === 'string';
};

export const readServerResolvedPageContext = (): ResolvedPageContext | null => {
  if (typeof document === 'undefined') return null;
  const element = document.getElementById('mobilbo-resolved-page-context');
  if (!element?.textContent) return null;
  try {
    const parsed: unknown = JSON.parse(element.textContent);
    return isResolvedPageContext(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

export type CanonicalEntityIds = Readonly<{
  brandId?: string | number;
  modelId?: string | number;
  modelServiceRelationshipId?: string | number;
  productId?: string | number;
  blogPostId?: string | number;
  deviceCategoryId?: string;
  staticSlug?: string;
  buybackModelSlug?: string;
}>;

const resolvedContextRequests = new Map<string, Promise<ResolvedPageContext>>();

const requestResolvedContext = (url: string, errorPrefix: string): Promise<ResolvedPageContext> => {
  const existing = resolvedContextRequests.get(url);
  if (existing) return existing;

  const request = fetch(url, { headers: { Accept: 'application/json' } })
    .then(async response => {
      if (!response.ok) throw new Error(`${errorPrefix}_${response.status}`);
      const payload: unknown = await response.json();
      const context = payload && typeof payload === 'object'
        ? (payload as { context?: unknown }).context
        : null;
      if (!isResolvedPageContext(context)) throw new Error(`${errorPrefix}_INVALID_RESPONSE`);
      return context;
    })
    .catch(error => {
      resolvedContextRequests.delete(url);
      throw error;
    });

  resolvedContextRequests.set(url, request);
  return request;
};

export const fetchCanonicalPageContext = async (
  pageArchetype: PageArchetype,
  ids: CanonicalEntityIds = {}
): Promise<ResolvedPageContext> => {
  const parameters = new URLSearchParams({ pageArchetype });
  Object.entries(ids).forEach(([key, value]) => {
    if (value != null && String(value).trim()) parameters.set(key, String(value));
  });
  return requestResolvedContext(
    `/api/public/canonical-page-context?${parameters.toString()}`,
    'CANONICAL_PROJECTION'
  );
};

export const fetchResolvedPageContext = async (path: string): Promise<ResolvedPageContext> => {
  return requestResolvedContext(
    `/api/public/resolved-page-context?path=${encodeURIComponent(path)}`,
    'ENTITY_RESOLUTION'
  );
};

export const projectBrowserStatePath = (context: ResolvedPageContext): string => {
  const parameters = new URLSearchParams();
  context.queryParameters
    .filter((entry) => entry.classification !== 'IDENTITY_LEGACY')
    .forEach((entry) => parameters.append(entry.name, entry.value));
  return `${context.canonicalPath}${parameters.size ? `?${parameters.toString()}` : ''}`;
};

export const navigateResolvedPage = (
  context: ResolvedPageContext,
  mode: 'push' | 'replace' | 'assign' = 'push',
  requestedPath?: string
) => {
  let browserPath = projectBrowserStatePath(context);
  if (requestedPath) {
    const requestedUrl = new URL(requestedPath, window.location.origin);
    if (requestedUrl.origin !== window.location.origin || requestedUrl.pathname !== context.canonicalPath) {
      throw new Error('RESOLVED_PAGE_PATH_MISMATCH');
    }
    browserPath = `${requestedUrl.pathname}${requestedUrl.search}${requestedUrl.hash}`;
  }
  // Direct requests keep their complete SSR projection. Once the governed context
  // has been resolved inside the hydrated application, repeating a full document
  // navigation only downloads and hydrates the same shell/catalog again.
  if (mode === 'assign') {
    window.location.assign(browserPath);
    return;
  }
  const method = mode === 'replace' ? 'replaceState' : 'pushState';
  window.history[method]({ resolvedPageContext: context }, '', browserPath);
  window.dispatchEvent(new PopStateEvent('popstate', { state: { resolvedPageContext: context } }));
};
